/**
 * Milestone celebration banner API client — Story 76.3a / 76.3b.
 *
 * Unlike the user-scoped notification domain (ADR-020 §6 — `notificationFetch`,
 * never sends `X-Resource-Tenant-Id`), the milestone-banner domain is
 * tenant-scoped: the BE filters by the active client via the standard
 * `X-Resource-Tenant-Id` header, which `axiosInstance` injects automatically.
 *
 * Contract (Story 76.3a Dev Notes / BE 76.3b):
 *   GET /api/v1/milestones/latest
 *        → { items: MilestoneBannerItem[] (≤ 3), kudosUrl: string | null }
 *   PUT /api/v1/milestones/{milestoneId}/acknowledgement
 *        → 204 No Content (idempotent on (UserId, MilestoneId))
 */

import { isAxiosError } from 'axios';
import axiosInstance from '../api/axiosInstance';
import {
  USE_MOCK_MILESTONE_BANNERS,
  mockFetchMilestoneBanners,
  mockAcknowledgeMilestoneBanner,
} from './milestoneBannerMock';
import type { MilestoneBannerResponse } from '../types/models/MilestoneBanner';

const BASE_PATH = '/api/v1/milestones';

export async function fetchMilestoneBanners(): Promise<MilestoneBannerResponse> {
  if (USE_MOCK_MILESTONE_BANNERS) {
    return mockFetchMilestoneBanners();
  }
  try {
    const res = await axiosInstance.get<MilestoneBannerResponse>(`${BASE_PATH}/latest`);
    return res.data;
  } catch (err) {
    if (isAxiosError(err) && err.response) {
      throw new Error(
        `fetchMilestoneBanners failed: ${err.response.status} ${err.response.statusText}`
      );
    }
    throw err instanceof Error ? err : new Error('fetchMilestoneBanners failed');
  }
}

export async function acknowledgeMilestoneBanner(milestoneId: string): Promise<void> {
  if (USE_MOCK_MILESTONE_BANNERS) {
    await mockAcknowledgeMilestoneBanner(milestoneId);
    return;
  }
  try {
    await axiosInstance.put(`${BASE_PATH}/${encodeURIComponent(milestoneId)}/acknowledgement`);
  } catch (err) {
    if (isAxiosError(err) && err.response) {
      throw new Error(
        `acknowledgeMilestoneBanner failed: ${err.response.status} ${err.response.statusText}`
      );
    }
    throw err instanceof Error ? err : new Error('acknowledgeMilestoneBanner failed');
  }
}
